import { Box, Button, ButtonMenu, ButtonMenuItem, Flex, Text } from '@pancakeswap/uikit'
import { useTranslation } from 'contexts/Localization'
import useTheme from 'hooks/useTheme'
import React, { useState } from 'react'
import { useFetchPriceGraphData } from 'state/swap/hooks'
import NoChartAvailable from './NoChartAvailable'
import PriceTradingChart from './PriceTradingChart'
import TokenDisplay from './TokenDisplay'
import { getTimeWindowChange } from './utils'
import { ReactComponent as CandleIcon } from '../../../../assets/images/candle.svg'
import { ReactComponent as ChartIcon } from '../../../../assets/images/chart.svg'
import { ChartViewMode } from 'state/user/actions'

const TIME_WINDOW_LABELS = { BASIC: ['1D', '1W', '1M', '1Y'], CANDLE: ['30M', '1D', '1W', '4M'] }
const TIME_WINDOW_DATA = { BASIC: ['24H', '1W', '1M', '1Y'], CANDLE: ['30m', '24h', '4h', '4d'] }

const PriceTradingBasicChart = ({
  token0Address,
  token1Address,
  isChartExpanded,
  inputCurrency,
  outputCurrency,
  isMobile,
  setChartView,
  chartMode,
}) => {
  const { theme } = useTheme()
  const [timeWindow, setTimeWindow] = useState<number>(2)

  const chartData = useFetchPriceGraphData(token0Address, token1Address, TIME_WINDOW_DATA[chartMode][timeWindow])

  const [hoverValue, setHoverValue] = useState<number | undefined>()
  const [hoverDate, setHoverDate] = useState<string | undefined>()

  const { changePercentage, changeValue, valueToDisplay, precision } = getTimeWindowChange(chartData?.data)
  const isChangePositive = changeValue >= 0
  const chartHeight = isChartExpanded ? 'calc(100% - 120px)' : 'auto'
  const {
    t,
    currentLanguage: { locale },
  } = useTranslation()
  const currentDate = new Date().toLocaleString(locale, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })

  // Sometimes we might receive array full of zeros for obscure tokens while trying to derive data
  // In that case chart is not useful to users
  const isBadData =
    chartData && chartData.length > 0 && chartData.every((priceObj) => !priceObj.value && !priceObj.open)

  if (isBadData) {
    return (
      <NoChartAvailable
        token0Address={token0Address}
        token1Address={token1Address}
        pairAddress=""
        isMobile={isMobile}
      />
    )
  }

  return (
    <>
      <Flex flexDirection="column" alignItems="flex-start" justifyContent="space-between" px="24px">
        <Flex flexDirection="column">
          <TokenDisplay
            value={chartData?.data?.length > 0 && (hoverValue?.toFixed(precision) || valueToDisplay)}
            inputSymbol={inputCurrency?.symbol}
            outputSymbol={outputCurrency?.symbol}
          />
        </Flex>
        <Flex justifyContent="space-between" width="100%" flexWrap="wrap" flexDirection={isMobile ? 'column' : 'row'}>
          <Flex>
            <Text color={isChangePositive ? 'success' : 'failure'} fontSize="12px" pr="10px">
              {`${isChangePositive ? '+' : ''}${changeValue} (${changePercentage}%)`}
            </Text>
            <Text color="text" fontSize="12px">
              {hoverDate || currentDate}
            </Text>
          </Flex>
        </Flex>
      </Flex>
      <Box height={chartHeight} p={isMobile ? '0px' : '16px 0 5px'} width="100%">
        <PriceTradingChart
          {...{
            token0Address,
            token1Address,
            timeWindow,
            setHoverValue,
            setHoverDate,
            viewMode: chartMode,
            isChangePositive,
            chartData,
          }}
        />
      </Box>
      <Flex
        flexDirection="column"
        alignItems="flex-start"
        justifyContent="space-between"
        px="24px"
        mt="0px"
        style={{ borderBottom: `1px solid ${theme.colors.cardBorder}` }}
      >
        <Flex alignItems="center" mb={isMobile ? '10px' : '0'}>
          <Button
            variant="subtle"
            onClick={() => setChartView(chartMode === 'BASIC' ? ChartViewMode.TRADING_VIEW : ChartViewMode.BASIC)}
            scale="xs"
            mr="5px"
            px="6px"
            style={{ backgroundColor: 'transparent', borderRadius: 5 }}
          >
            {chartMode === 'BASIC' ? (
              <CandleIcon color={theme.colors.primary} />
            ) : (
              <ChartIcon color={theme.colors.primary} />
            )}
          </Button>
          <ButtonMenu activeIndex={timeWindow} onItemClick={setTimeWindow} scale="xs" variant="subtle">
            {/* border: colors.input,  */}
            {TIME_WINDOW_LABELS[chartMode].map((item) => (
              <ButtonMenuItem key={`${item}`}>{t(item)}</ButtonMenuItem>
            ))}
          </ButtonMenu>
        </Flex>
      </Flex>
    </>
  )
}

export default PriceTradingBasicChart
